import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Building2, CornerDownLeft, Search } from 'lucide-react';
import { prospects as prospectsApi } from '../api/client.ts';
import { Kbd, ScoreValue, cx } from './primitives.tsx';

export interface PaletteDestination {
  to: string;
  label: string;
  icon: ReactNode;
  hint?: string;
}

/**
 * Ctrl+K: pular para qualquer empresa pelo nome, ou para qualquer tela.
 *
 * Quem prospecta passa o dia indo e voltando entre a fila e as fichas; abrir
 * a listagem, filtrar e clicar é o caminho longo para "a hamburgueria que
 * respondeu agora". A busca é a mesma da listagem (`GET /prospects?search=`),
 * com espera de 200 ms entre teclas para não disparar uma consulta por letra.
 */
export function CommandPalette({
  open,
  onClose,
  destinations,
}: {
  open: boolean;
  onClose: () => void;
  destinations: PaletteDestination[];
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [cursor, setCursor] = useState(0);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
      setTerm('');
      setDebounced('');
      setCursor(0);
      window.setTimeout(() => input.current?.focus(), 0);
    }
    if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term.trim()), 200);
    return () => window.clearTimeout(timer);
  }, [term]);

  const found = useQuery({
    queryKey: ['palette', debounced],
    queryFn: () => prospectsApi.list({ search: debounced, limit: 6, sort: 'score' }),
    enabled: open && debounced.length >= 2,
    staleTime: 15_000,
  });

  const pages = useMemo(() => {
    const folded = fold(term);
    return destinations.filter((item) => !folded || fold(item.label).includes(folded)).slice(0, 8);
  }, [destinations, term]);

  const companies = debounced.length >= 2 ? (found.data?.prospects ?? []) : [];
  const results: { key: string; to: string; node: ReactNode }[] = [
    ...companies.map((prospect) => ({
      key: `p-${prospect.id}`,
      to: `/prospects/${prospect.id}`,
      node: (
        <>
          <Building2 aria-hidden="true" className="size-4 text-[var(--color-ink-faint)]" />
          <span className="min-w-0 flex-1 truncate">
            {prospect.name}
            <span className="text-[var(--color-ink-faint)]">{prospect.city ? ` — ${prospect.city}` : ''}</span>
          </span>
          <ScoreValue value={prospect.final_score} />
        </>
      ),
    })),
    ...pages.map((item) => ({
      key: `t-${item.to}`,
      to: item.to,
      node: (
        <>
          <span className="text-[var(--color-ink-faint)] [&_svg]:size-4">{item.icon}</span>
          <span className="flex-1">{item.label}</span>
          {item.hint && <span className="text-xs text-[var(--color-ink-faint)]">{item.hint}</span>}
        </>
      ),
    })),
  ];
  const safeCursor = Math.min(cursor, Math.max(0, results.length - 1));

  function go(to: string) {
    onClose();
    navigate(to);
  }

  return (
    <dialog
      ref={ref}
      aria-label="Ir para"
      onClose={onClose}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        // Clique no fundo (fora da caixa) fecha.
        if (event.target === ref.current) onClose();
      }}
      className="enter-up mx-auto mt-[12vh] w-[min(600px,calc(100vw-32px))] overflow-hidden rounded-[var(--radius-lg)] border-0 bg-[var(--color-raised)] p-0 text-[var(--color-ink)] shadow-[var(--shadow-pop)]"
    >
      {open && (
        <div className="flex flex-col">
          <div className="flex items-center gap-3 border-b border-[var(--color-line)] px-4">
            <Search aria-hidden="true" className="size-4 shrink-0 text-[var(--color-ink-faint)]" />
            <input
              ref={input}
              value={term}
              onChange={(event) => {
                setTerm(event.target.value);
                setCursor(0);
              }}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  setCursor((value) => Math.min(value + 1, results.length - 1));
                } else if (event.key === 'ArrowUp') {
                  event.preventDefault();
                  setCursor((value) => Math.max(value - 1, 0));
                } else if (event.key === 'Enter' && results[safeCursor]) {
                  event.preventDefault();
                  go(results[safeCursor].to);
                }
              }}
              placeholder="Buscar empresa pelo nome, ou ir para uma tela"
              aria-label="Buscar"
              role="combobox"
              aria-expanded={results.length > 0}
              aria-controls="paleta-resultados"
              aria-activedescendant={results[safeCursor] ? `paleta-${results[safeCursor].key}` : undefined}
              className="h-12 flex-1 bg-transparent text-[15px] placeholder:text-[var(--color-ink-faint)] focus:outline-none"
            />
            <Kbd>Esc</Kbd>
          </div>
          <ul id="paleta-resultados" role="listbox" className="max-h-[50vh] overflow-y-auto p-2">
            {debounced.length >= 2 && found.isFetching && companies.length === 0 && (
              <li className="px-3 py-2 text-sm text-[var(--color-ink-faint)]">Buscando…</li>
            )}
            {results.length === 0 && !found.isFetching && (
              <li className="px-3 py-2 text-sm text-[var(--color-ink-faint)]">Nada com esse nome.</li>
            )}
            {results.map((item, index) => (
              <li
                key={item.key}
                id={`paleta-${item.key}`}
                role="option"
                aria-selected={index === safeCursor}
                onMouseEnter={() => setCursor(index)}
                onClick={() => go(item.to)}
                className={cx(
                  'flex cursor-pointer items-center gap-3 rounded-[var(--radius-sm)] px-3 py-2 text-sm',
                  index === safeCursor && 'bg-[var(--color-brand-soft)] shadow-[inset_0_0_0_1px_rgb(10_92_255/0.35)]',
                )}
              >
                {item.node}
                {index === safeCursor && <CornerDownLeft aria-hidden="true" className="size-3.5 text-[var(--color-ink-faint)]" />}
              </li>
            ))}
          </ul>
        </div>
      )}
    </dialog>
  );
}

function fold(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}
